import { Box, LinearProgress, useTheme } from "@mui/material";
import { useKeycloak } from "@react-keycloak/web";
import { useQueryClient } from "@tanstack/react-query";
import { useAtom, useAtomValue } from "jotai";
import { useEffect } from "react";

import { AI_GENERATION_COMPLETION_STATUS, ARTIFACT_TYPES } from "../../constants";
import { artifactAtom } from "../../stores/jotai/artifacts/artifacts";
import {
  baseAtom,
  setIsInterviewInEditMode,
  setSelectedArtifactType
} from "../../stores/jotai/base";
import { aiInsightGenerationProgressAtom } from "../../stores/jotai/insights/aiInsightGenerationStatus";
import { useGetMetaArtifacts } from "../../tanstackQuery/queries/artifacts";
import TableHeader from "./analysis/component/Tableheader";
import MaterialAnalysisEditor from "./analysis/material-analysis-editor";
import MaterialNoteEditor from "./material-note-editor";

const TabPanel = (props: any) => {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3, height: "100%" }}>{children}</Box>}
    </Box>
  );
};

export default function NoteAndAnalysisSection(props) {
  const [base, setBase] = useAtom(baseAtom);
  const [artifacts] = useAtom(artifactAtom);
  const aiInsightProgress = useAtomValue(aiInsightGenerationProgressAtom);
  const theme = useTheme();
  const { keycloak } = useKeycloak();
  const client = useQueryClient();

  const selectedInterviewID = base?.selectedInterview;
  const analysis = artifacts?.metaAnalysis?.[selectedInterviewID];
  const tabIndex = base.selectedArtifactType.includes("notes") ? 0 : 1;

  const {
    data: metaArtifactData,
    isLoading,
    isError,
  } = useGetMetaArtifacts(
    { accessToken: keycloak.token },
    { artifactID: analysis?.artifactID, isMappedByParent: false},
    {
      enabled: !!analysis?.artifactID,
      retry: 1,
      retryDelay: 3000,
    }
  );

  const isAnalysisAvailable = !!metaArtifactData && !isError;
  const progressByInterviewID = aiInsightProgress?.[ARTIFACT_TYPES.TRANSCRIPT]?.[selectedInterviewID];

  useEffect(() => {
    if (progressByInterviewID === AI_GENERATION_COMPLETION_STATUS.SUCCESSFULL) {
      client.invalidateQueries({ queryKey: ["artifacts"] });
      client.invalidateQueries({ queryKey: ["meta-artifacts"] });
    }
  }, [progressByInterviewID, client]);

  useEffect(()=>{
    if(!isAnalysisAvailable){
      setSelectedArtifactType(setBase, "notes");
    }
  },[isAnalysisAvailable])

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        background: "white",
        border: `1px solid ${theme.custom.borderGrey}`,
        borderRadius: "4px",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <TableHeader isAnalysisAvailable={isAnalysisAvailable} />
      <TabPanel
        sx={{ flex: 1, minHeight: 0, overflow: "auto" }}
        value={tabIndex}
        index={1}
      >
        {isLoading ? (
          <LinearProgress />
        ) : (
          <MaterialAnalysisEditor metaArtifactData={metaArtifactData} />
        )}
      </TabPanel>
      <TabPanel
        sx={{
          height: "calc(100% - 105px)",
          padding: "12px 6px 0",
        }}
        value={tabIndex}
        index={0}
      >
        <MaterialNoteEditor
          {...props}
          setEditingNote={() => setIsInterviewInEditMode(setBase, false)}
          editingNote={base?.interviewEditMode}
          interviewData={props?.allInterview}
          disableDefaultBorder={true}
        />
      </TabPanel>
    </Box>
  );
}
